import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useI18N, useResponsive } from "@sundae/react-hooks";
import {
  FadeInBasic,
  Notice,
  Text,
  Tooltip as ToolkitTooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import cx from "classnames";
import { FC, useCallback, useMemo, useState } from "react";
import { Chart } from "react-chartjs-2";
import { TFunction } from "react-i18next";

import { useCalculateContributionOutcome } from "../../../hooks/taste-test/useCalculateContributionOutcome";
import { useTasteTestTargetPriceChart } from "../../../hooks/taste-test/useTasteTestTargetPriceChart";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import useTargetPrice from "../../../pages/TasteTestDetail/hooks/useTargetPrice";
import { formatCurrencyToAda, percentageI18n } from "../../../utils/number-format";
import TasteTestEmptyChartView from "../TasteTestEmptyChartView";
import TasteTestTargetPriceInput from "../TasteTestTargetPriceInput";
import { getTasteTestTargetPriceChartOptions } from "./getChartOptions";

ChartJS.register(
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip
);

export type TTasteTestTargetPriceChartConfig = {
  currentLocale?: string;
  isDark?: boolean;
  maxX?: number;
  minX?: number;
  responsive?: ReturnType<typeof useResponsive>;
  t?: TFunction;
};

const TasteTestTargetPriceChart: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: {
      tasteTest,
      tasteTestStatus: { isTasteTestInactive, isTasteTestPending },
      tasteTestPersonalization,
    },
  } = useTasteTestContext();
  const { targetPrice } = useTargetPrice();
  const { calculateTargetPrice } = useCalculateContributionOutcome({
    currentBalance: tasteTest?.currentBalance,
  });
  const { chartConfig, chartData } = useTasteTestTargetPriceChart();
  const [editTargetPrice, setEditTargetPrice] = useState(false);
  const currentTargetPrice = calculateTargetPrice();
  const hasTargetPriceInformation = !!currentTargetPrice.raw;

  const handleTargetPriceDialog = useCallback(() => {
    if (isTasteTestInactive) {
      return;
    }
    setEditTargetPrice(true);
  }, [isTasteTestInactive]);

  /**
   * Calculates the delta between the current target price and the user's target price.
   */
  const targetPriceDelta = useMemo(() => {
    return targetPrice / currentTargetPrice.raw;
  }, [targetPrice, currentTargetPrice.raw]);

  /**
   * Renders the delta between the current target price and the user's target price.
   */
  const targetPriceDeltaI18n = useMemo(() => {
    return percentageI18n(Math.abs(1 - targetPriceDelta), currentLocale);
  }, [targetPriceDelta, currentLocale]);

  /**
   * Renders the label of the button to set the target price.
   */
  const memoizedTargetPriceButtonLabel = useMemo(() => {
    if (isTasteTestInactive) {
      return t("targetPrice", {
        context: "inactiveTooltip",
        price: formatCurrencyToAda(currentTargetPrice.raw, currentLocale),
      });
    }
    return t("targetPrice", { context: targetPrice ? "update" : "set" });
  }, [isTasteTestInactive, targetPrice, t]);

  /**
   * Renders the label below the user's target price.
   */
  const memoizedTargetPriceInformation = useMemo(() => {
    if (isTasteTestInactive) {
      return (
        <Text className="flex-1 italic" tag="span" size="sm" variant="muted">
          {t("targetPrice", {
            aboveBelow: currentTargetPrice.raw > targetPrice ? "above" : "below",
            context: targetPrice ? "inactive" : "inactiveUnset",
            percentage: targetPriceDeltaI18n,
            price: formatCurrencyToAda(currentTargetPrice.raw, currentLocale),
          })}
        </Text>
      );
    }

    if (!targetPrice) {
      return (
        <Text className="flex items-center gap-1 italic" tag="span" size="sm" variant="muted">
          {t("targetPrice", { context: "qa" })}
          <ToolkitTooltip
            trigger={
              <Text tag="div" variant="muted" className="w-fit cursor-help">
                <InformationCircleIcon className="h-4 w-4 text-current" />
              </Text>
            }
            tooltipLabel={t("targetPrice", { context: "qaTooltip" })}
          />
        </Text>
      );
    }

    if (Number(currentTargetPrice.raw.toFixed(6)) === targetPrice) {
      return (
        <Text tag="span" size="sm" variant="muted">
          {t("targetPrice", { context: "equalsCurrent" })}
        </Text>
      );
    }

    return (
      <Text tag="span" size="sm" variant="muted">
        {t("targetPrice", {
          aboveBelow: targetPrice > currentTargetPrice.raw ? "above" : "below",
          context: "aboveBelowCurrent",
          percentage: targetPriceDeltaI18n,
          price: formatCurrencyToAda(currentTargetPrice.raw, currentLocale),
        })}
      </Text>
    );
  }, [
    currentTargetPrice,
    currentLocale,
    isTasteTestInactive,
    targetPriceDeltaI18n,
    targetPrice,
    t,
  ]);

  /**
   * Renders the label of the user's target price.
   */
  const memoizedTargetPriceLabel = useMemo(() => {
    if (isTasteTestInactive && !targetPrice) {
      return (
        <div onClick={handleTargetPriceDialog}>
          {t("targetPrice", { context: "inactiveUnsetTitle" })}
        </div>
      );
    }

    if (targetPrice) {
      return (
        <Text className="inline-block h-5" tag="div" size="sm">
          {t("targetPrice", { context: "individual" })}:{" "}
          {editTargetPrice ? (
            <TasteTestTargetPriceInput onClose={() => setEditTargetPrice(false)} />
          ) : (
            <ToolkitTooltip
              trigger={
                <div
                  className="inline"
                  style={{
                    color: tasteTestPersonalization?.primaryColor ?? "",
                  }}
                >
                  <Text
                    role="button"
                    tag="span"
                    size="sm"
                    variant={
                      isTasteTestInactive
                        ? "normal"
                        : tasteTestPersonalization?.primaryColor
                        ? "current"
                        : "info"
                    }
                    className={cx("border-b border-dotted", {
                      "cursor-pointer border-b-primary": !isTasteTestInactive,
                      "cursor-not-allowed border-b-black dark:border-b-gray-200":
                        isTasteTestInactive,
                    })}
                    style={{
                      borderColor: tasteTestPersonalization?.primaryColor ?? "",
                    }}
                    onClick={handleTargetPriceDialog}
                  >
                    {formatCurrencyToAda(targetPrice, currentLocale)}
                  </Text>
                </div>
              }
              tooltipLabel={memoizedTargetPriceButtonLabel}
            />
          )}
        </Text>
      );
    }

    return (
      <Text tag="span" size="sm">
        {t("targetPrice", { context: "unset" })}{" "}
        {editTargetPrice ? (
          <TasteTestTargetPriceInput onClose={() => setEditTargetPrice(false)} />
        ) : (
          <Text
            role="button"
            variant={tasteTestPersonalization?.primaryColor ? "current" : "info"}
            tag="span"
            hoverable
            size="sm"
            className="cursor-pointer border-b border-dotted border-b-primary"
            style={{
              borderColor: tasteTestPersonalization?.primaryColor ?? "",
            }}
            onClick={handleTargetPriceDialog}
          >
            {memoizedTargetPriceButtonLabel}
          </Text>
        )}
      </Text>
    );
  }, [currentLocale, editTargetPrice, isTasteTestInactive, targetPrice, t]);

  /* -------------------------------------------------------------------------------------------------
   * Notice for no available Price Information
   * -----------------------------------------------------------------------------------------------*/
  if (!hasTargetPriceInformation) {
    return (
      <TasteTestEmptyChartView
        title={t("detail.priceChart.noPriceData", { context: "title" })}
        description={t("detail.priceChart.noPriceData")}
      />
    );
  }

  return (
    <FadeInBasic className="flex w-full flex-col gap-4">
      {/* -------------------------------------------------------------------------------------------------
       * Target Price Title + Input
       * -----------------------------------------------------------------------------------------------*/}
      <div className="relative z-10 flex items-center justify-between space-x-2 rounded-lg">
        <div className="grid flex-1 gap-1">
          <Text className="relative w-fit" tag="div" size="sm">
            {/* -------------------------------------------------------------------------------------------------
             * Overlay for Target Price Input
             * -----------------------------------------------------------------------------------------------*/}
            {editTargetPrice && (
              <div
                className={cx(
                  "absolute inset-0 z-20 -ml-1 -mr-1 w-[calc(100%+1rem)] rounded-lg motion-safe:animate-dialog-overlay-show",
                  "backdrop-blur-sm",
                  toolkitCx.layout.background.overlay
                )}
              />
            )}
            {memoizedTargetPriceLabel}
          </Text>
          {memoizedTargetPriceInformation}
        </div>
      </div>

      <div className="relative">
        {/* -------------------------------------------------------------------------------------------------
         * Notice for Pending (Upcoming) Taste Test
         * -----------------------------------------------------------------------------------------------*/}
        {isTasteTestPending && (
          <div
            className={cx(
              "absolute z-10 max-w-[75%] rounded-lg text-center",
              toolkitCx.layout.background.card,
              toolkitCx.recipes.center.both()
            )}
          >
            <Notice
              type="warning"
              size="sm"
              title={t("detail.status.pending", { context: "title" })}
            >
              <Text tag="span" size="xs" variant="current">
                {t("detail.status.pending")}
              </Text>
            </Notice>
          </div>
        )}

        {/* -------------------------------------------------------------------------------------------------
         * Chart
         * -----------------------------------------------------------------------------------------------*/}
        <div
          className={cx("relative mx-auto grid w-full max-w-3xl gap-4 lg:mx-0 lg:h-full", {
            "pointer-events-none blur-sm": isTasteTestPending || !hasTargetPriceInformation,
          })}
        >
          <Chart
            type="line"
            options={getTasteTestTargetPriceChartOptions(chartConfig)}
            data={chartData}
          />
        </div>
      </div>
    </FadeInBasic>
  );
};

export default TasteTestTargetPriceChart;
